import React, { useState, useEffect } from 'react'
import {
  Container, Row, Col, Card, CardBody, CardTitle, CardText,
  Button, Table, Badge, Progress, Alert, Modal, ModalHeader,
  ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormText,
  Spinner, UncontrolledTooltip, Collapse
} from 'reactstrap';
import { Link } from "react-router-dom";

import {
  Sync, Refresh, Cancel, CheckCircle, Error, Warning,
  Info, Settings, GetApp, CloudDownload, CloudOff,
  CloudDone, Timeline, BarChart, DataUsage,
  Storage, Speed, BugReport, Build, ListAlt,
  Cached, CloudUpload
} from '@material-ui/icons';
import axios from "../../../services/axios"
import swal from 'sweetalert';
import Loader from "../../../components/Loader";

function EmisSyncManagement() {
  const [loading, setLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncProgress, setSyncProgress] = useState(null);
  const [syncModal, setSyncModal] = useState(false);
  const [fixZonesModal, setFixZonesModal] = useState(false);
  const [clearCacheModal, setClearCacheModal] = useState(false);
  const [syncOptions, setSyncOptions] = useState({
    force: false,
    dry_run: false
  });
  const [testConnectionResult, setTestConnectionResult] = useState(null);
  const [collapseStats, setCollapseStats] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [recentSyncResult, setRecentSyncResult] = useState(null);

  const [schedule, setSchedule] = useState(null);
  const [scheduleModal, setScheduleModal] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    schedule_type: 'daily',
    time: '00:00',
    day_of_week: 0,
    day_of_month: 1,
    enabled: true,
    force_sync: false,
    send_notifications: true,
    notification_emails: '',
    timezone: 'UTC'
  });

  // Add function to fetch schedule
  const fetchSchedule = async () => {
    try {
      const { data } = await axios.get('emis/schedule');
      if (data.success && data.data) {
        setSchedule(data.data);
        setScheduleForm({
          schedule_type: data.data.schedule_type || 'daily',
          time: data.data.time || '00:00',
          day_of_week: data.data.day_of_week || 0,
          day_of_month: data.data.day_of_month || 1,
          enabled: data.data.enabled !== false,
          force_sync: data.data.force_sync || false,
          send_notifications: data.data.send_notifications !== false,
          notification_emails: data.data.notification_emails || '',
          timezone: data.data.timezone || 'UTC'
        });
      }
    } catch (error) {
      console.error('Error fetching schedule:', error);
    }
  };

  // Add function to save schedule
  const saveSchedule = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post('emis/schedule', scheduleForm);
      if (data.success) {
        swal({
          title: "Schedule Saved!",
          text: data.message,
          icon: "success",
          timer: 3000,
          button: false
        });
        setSchedule(data.data);
        setScheduleModal(false);
        fetchSchedule(); // Refresh
      } else {
        swal({
          title: "Error!",
          text: data.message,
          icon: "error",
          timer: 3000,
          button: false
        });
      }
    } catch (error) {
      swal({
        title: "Error!",
        text: "Failed to save schedule",
        icon: "error",
        timer: 3000,
        button: false
      });
    } finally {
      setLoading(false);
    }
  };

  // Add function to test schedule
  const testSchedule = async () => {
    try {
      const { data } = await axios.get('emis/schedule/test');
      if (data.success) {
        swal({
          title: "Schedule Test",
          text: data.data.human_readable + `\nNext run: ${data.data.next_run}`,
          icon: "info",
          button: true
        });
      }
    } catch (error) {
      console.error('Error testing schedule:', error);
    }
  };

  // Call fetchSchedule in useEffect
  useEffect(() => {
    fetchSyncStatus();
    fetchSyncProgress();
    fetchSchedule();
  }, []);

  // Fetch initial data
  // Add this useEffect to handle polling only when syncing
  useEffect(() => {
    let interval;

    if (isSyncing) {
      // Poll more frequently when syncing
      interval = setInterval(fetchSyncProgress, 2000);
    } else {
      // Poll less frequently when not syncing
      interval = setInterval(fetchSyncProgress, 30000); // Every 30 seconds
    }

    return () => clearInterval(interval);
  }, [isSyncing]);

  // Update initial useEffect to only fetch once
  useEffect(() => {
    fetchSyncStatus();
    fetchSyncProgress();
    // Don't start interval here, let the above useEffect handle it
  }, []);

  // Fetch sync status
  const fetchSyncStatus = async () => {
    try {
      const { data } = await axios.get('emis/sync/status');
      if (data.success) {
        setSyncStatus(data.data);
        if (data.data.recent_stats) {
          setRecentSyncResult(data.data.recent_stats);
        }
      }
    } catch (error) {
      console.error('Error fetching sync status:', error);
    }
  };

  // Fetch sync progress
  // Fix fetchSyncProgress to return data
  const fetchSyncProgress = async () => {
    try {
      const { data } = await axios.get('emis/sync/progress');
      console.log('Sync progress data:', data);
      if (data.success) {
        setSyncProgress(data.data);
        setIsSyncing(data.data.is_running || false);
        // If sync just completed, refresh status
        if (!data.data.is_running && data.data.status === 'completed') {
          fetchSyncStatus();
        }
      }
      return data; // Return data for checking in startSync
    } catch (error) {
      console.error('Error fetching sync progress:', error);
      setIsSyncing(false);
      return null;
    }
  };

  // Test EMIS API connection
  const testConnection = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('emis/sync/test-connection');
      console.log('Test connection data:', data);
      setTestConnectionResult(data);
      if (data.success) {
        swal({
          title: "Connection Successful!",
          text: `Connected to EMIS API with ${data.total_institutions} institutions available.`,
          icon: "success",
          timer: 3000,
          button: false
        });
      } else {
        swal({
          title: "Connection Failed!",
          text: data.message || "Unable to connect to EMIS API",
          icon: "error",
          timer: 3000,
          button: false
        });
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      swal({
        title: "Error!",
        text: "Failed to test connection",
        icon: "error",
        timer: 3000,
        button: false
      });
    } finally {
      setLoading(false);
    }
  };

  // Start synchronization
  // Simplify startSync function
  const startSync = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post('emis/sync', syncOptions);
      console.log('Start sync data:', data);
      if (data.success) {
        swal({
          title: syncOptions.dry_run ? "Dry Run Started!" : "Sync Started!",
          text: data.message || (syncOptions.dry_run ? "Dry run in progress" : "Synchronization process has started"),
          icon: "success",
          timer: 3000,
          button: false
        });

        // Set syncing to true - the main polling interval will handle progress
        setIsSyncing(true);
        setSyncModal(false);

        // Immediately fetch progress to update UI
        setTimeout(() => {
          fetchSyncProgress();
        }, 1000);

      } else {
        swal({
          title: "Sync Failed!",
          text: data.message || "Failed to start synchronization",
          icon: "error",
          timer: 3000,
          button: false
        });
      }
    } catch (error) {
      console.error('Error starting sync:', error);
      swal({
        title: "Error!",
        text: "Failed to start synchronization",
        icon: "error",
        timer: 3000,
        button: false
      });
    } finally {
      setLoading(false);
    }
  };

  // Cancel sync
  const cancelSync = async () => {
    try {
      const { data } = await axios.post('emis/sync/cancel');
      if (data.success) {
        swal({
          title: "Sync Cancelled!",
          text: data.message || "Synchronization has been cancelled",
          icon: "info",
          timer: 3000,
          button: false
        });
        setIsSyncing(false);
        fetchSyncStatus();
      }
    } catch (error) {
      swal({
        title: "Error!",
        text: "Failed to cancel synchronization",
        icon: "error",
        timer: 3000,
        button: false
      });
    }
  };

  // Clear sync cache
  const clearCache = async () => {
    try {
      const { data } = await axios.post('emis/sync/clear-cache');
      if (data.success) {
        swal({
          title: "Cache Cleared!",
          text: data.message || "Sync cache has been cleared",
          icon: "success",
          timer: 3000,
          button: false
        });
        fetchSyncStatus();
        setClearCacheModal(false);
      }
    } catch (error) {
      swal({
        title: "Error!",
        text: "Failed to clear cache",
        icon: "error",
        timer: 3000,
        button: false
      });
    }
  };

  // Fix zones with sector_id
  const fixZones = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post('emis/sync/fix-zones');
      if (data.success) {
        swal({
          title: "Zones Fixed!",
          text: data.message || `Updated ${data.updated_count} zones with sector_id`,
          icon: "success",
          timer: 3000,
          button: false
        });
        fetchSyncStatus();
      } else {
        swal({
          title: "Failed!",
          text: data.message || "Failed to fix zones",
          icon: "error",
          timer: 3000,
          button: false
        });
      }
    } catch (error) {
      swal({
        title: "Error!",
        text: "Failed to fix zones",
        icon: "error",
        timer: 3000,
        button: false
      });
    } finally {
      setLoading(false);
      setFixZonesModal(false);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'success':
      case 'completed':
      case 'connected':
        return <Badge color="success" pill><CheckCircle className="mr-1" /> {status}</Badge>;
      case 'failed':
      case 'error':
      case 'disconnected':
        return <Badge color="danger" pill><Error className="mr-1" /> {status}</Badge>;
      case 'running':
        return <Badge color="warning" pill><Sync className="mr-1" spin /> {status}</Badge>;
      case 'cancelled':
        return <Badge color="secondary" pill><Cancel className="mr-1" /> {status}</Badge>;
      case 'never_synced':
        return <Badge color="info" pill><Info className="mr-1" /> Never Synced</Badge>;
      default:
        return <Badge color="info" pill><Info className="mr-1" /> {status || 'Unknown'}</Badge>;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString || dateString === 'Never' || dateString.includes('Error')) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  // Toggle sync modal
  const toggleSyncModal = () => {
    setSyncModal(!syncModal);
    if (!syncModal) {
      setSyncOptions({ force: false, dry_run: false });
    }
  };

  // Toggle fix zones modal
  const toggleFixZonesModal = () => {
    setFixZonesModal(!fixZonesModal);
  };

  // Toggle clear cache modal
  const toggleClearCacheModal = () => {
    setClearCacheModal(!clearCacheModal);
  };

  // Action Card Component
  const ActionCard = ({ title, description, icon: Icon, color, onClick, disabled = false, loading = false }) => (
    <Card className={`border-${color} shadow-sm h-100`}>
      <CardBody className="d-flex flex-column">
        <div className={`bg-${color}-light rounded-circle p-3 mb-3 align-self-start`}>
          <Icon className={`text-${color}`} style={{ fontSize: '2rem' }} />
        </div>
        <CardTitle tag="h5" className="mb-2">{title}</CardTitle>
        <CardText className="text-muted mb-3 flex-grow-1">{description}</CardText>
        <Button
          color={color}
          onClick={onClick}
          disabled={disabled || loading}
          className="mt-auto"
        >
          {loading ? (
            <>
              <Spinner size="sm" className="mr-1" /> Processing...
            </>
          ) : (
            <>
              <Icon className="mr-1" /> {title}
            </>
          )}
        </Button>
      </CardBody>
    </Card>
  );

  return (
    <Container fluid className="p-4">
      {loading && <Loader />}

      {/* Page Header */}
      <Row className="align-items-center mb-4">
        <Col>
          <h2 className="page-title">
            <CloudDownload className="mr-2" />
            EMIS Data Synchronization
          </h2>
          <p className="text-muted mb-0">
            Synchronize school data between EMIS API and local database
          </p>
        </Col>
      </Row>

      {/* Connection Status Alert */}
      {testConnectionResult && (
        <Row className="mb-4">
          <Col>
            <Alert color={testConnectionResult.success ? "success" : "danger"}>
              <div className="d-flex align-items-center">
                {testConnectionResult.success ? (
                  <CheckCircle className="mr-2" />
                ) : (
                  <CloudOff className="mr-2" />
                )}
                <div>
                  <h5 className="alert-heading mb-1">
                    {testConnectionResult.success ? "Connected to EMIS API" : "Connection Failed"}
                  </h5>
                  <p className="mb-0">{testConnectionResult.message}</p>
                  {testConnectionResult.total_institutions && (
                    <small className="text-muted">
                      Total institutions available: {testConnectionResult.total_institutions}
                    </small>
                  )}
                </div>
              </div>
            </Alert>
          </Col>
        </Row>
      )}

        {schedule && (
        <Card className="mt-4 border-info">
          <CardBody>
            <CardTitle tag="h5">
              <Timeline className="mr-2" /> Current Schedule
            </CardTitle>
            <Table borderless size="sm">
              <tbody>
                <tr>
                  <td><strong>Type:</strong></td>
                  <td>{schedule.schedule_type}</td>
                  <td><strong>Status:</strong></td>
                  <td>
                    {schedule.enabled ? (
                      <Badge color="success">Active</Badge>
                    ) : (
                      <Badge color="secondary">Disabled</Badge>
                    )}
                  </td>
                </tr>
                {schedule.time && (
                  <tr>
                    <td><strong>Time:</strong></td>
                    <td>{schedule.time}</td>
                    <td><strong>Force Sync:</strong></td>
                    <td>{schedule.force_sync ? 'Yes' : 'No'}</td>
                  </tr>
                )}
                <tr>
                  <td><strong>Timezone:</strong></td>
                  <td>{schedule.timezone}</td>
                  <td><strong>Last Updated:</strong></td>
                  <td>{new Date(schedule.updated_at).toLocaleString()}</td>
                </tr>
              </tbody>
            </Table>
            <Button color="primary" size="sm" onClick={() => setScheduleModal(true)}>
              <Timeline className="mr-1" /> Edit Schedule
            </Button>
          </CardBody>
        </Card>
      )}

      {/* Sync Progress Section */}
      {isSyncing && syncProgress && (
        <Row className="mb-4">
          <Col>
            <Card className="border-warning shadow">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <CardTitle tag="h5" className="mb-0">
                    <Sync className="mr-2 spin" /> Synchronization in Progress
                    {syncOptions.dry_run && <Badge color="info" className="ml-2">Dry Run</Badge>}
                  </CardTitle>
                  <Button color="danger" size="sm" onClick={cancelSync}>
                    <Cancel className="mr-1" /> Cancel Sync
                  </Button>
                </div>

                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>
                      <strong>Status:</strong> {syncProgress.status || 'Processing...'}
                    </span>
                    <span>
                      {syncProgress.percentage || 0}% ({syncProgress.processed || 0} processed)
                    </span>
                  </div>
                  <Progress
                    value={syncProgress.percentage || 0}
                    color="warning"
                    animated
                    striped
                  />
                </div>

                {syncProgress.current_school && (
                  <Alert color="light" className="mb-0">
                    <div className="d-flex align-items-center">
                      <GetApp className="mr-2 text-primary" />
                      <span>
                        <strong>Current:</strong> {syncProgress.current_school}
                      </span>
                    </div>
                  </Alert>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Recent Sync Result */}
      {recentSyncResult && !isSyncing && (
        <Row className="mb-4">
          <Col>
            <Card className="border-success shadow">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <CardTitle tag="h5" className="mb-0">
                    <CloudDone className="mr-2" /> Last Sync Result
                  </CardTitle>
                  <small className="text-muted">
                    {formatDate(recentSyncResult.timestamp)}
                  </small>
                </div>

                <Row>
                  <Col md={3} className="text-center mb-3 mb-md-0">
                    <div className="display-4 text-success">{recentSyncResult.total_processed || 0}</div>
                    <small className="text-muted">Total Processed</small>
                  </Col>
                  <Col md={9}>
                    <div className="d-flex justify-content-between mb-2">
                      <div className="text-center">
                        <Badge color="success" pill className="p-2">
                          <div className="display-6">{recentSyncResult.created || 0}</div>
                        </Badge>
                        <div className="mt-1">
                          <small>Created</small>
                        </div>
                      </div>
                      <div className="text-center">
                        <Badge color="info" pill className="p-2">
                          <div className="display-6">{recentSyncResult.updated || 0}</div>
                        </Badge>
                        <div className="mt-1">
                          <small>Updated</small>
                        </div>
                      </div>
                      <div className="text-center">
                        <Badge color="secondary" pill className="p-2">
                          <div className="display-6">{recentSyncResult.skipped || 0}</div>
                        </Badge>
                        <div className="mt-1">
                          <small>Skipped</small>
                        </div>
                      </div>
                      <div className="text-center">
                        <Badge color={recentSyncResult.errors > 0 ? "danger" : "success"} pill className="p-2">
                          <div className="display-6">{recentSyncResult.errors || 0}</div>
                        </Badge>
                        <div className="mt-1">
                          <small>Errors</small>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Action Cards */}
      <Row className="mb-4">
        <Col md={3} className="mb-4">
          <ActionCard
            title="Test Connection"
            description="Test connection to EMIS API"
            icon={Speed}
            color="info"
            onClick={testConnection}
            disabled={isSyncing}
            loading={loading}
          />
        </Col>
        <Col md={3} className="mb-4">
          <ActionCard
            title="Start Sync"
            description="Start data synchronization"
            icon={Sync}
            color="success"
            onClick={toggleSyncModal}
            disabled={isSyncing}
          />
        </Col>
        <Col md={3} className="mb-4">
          <ActionCard
            title="Schedule Sync"
            description="Configure automatic sync schedule"
            icon={Timeline}
            color="primary"
            onClick={() => setScheduleModal(true)}
          />
        </Col>
        {/* <Col md={3} className="mb-4">
          <ActionCard
            title="Fix Zones"
            description="Fix zones with missing sector IDs"
            icon={Build }
            color="warning"
            onClick={toggleFixZonesModal}
            disabled={isSyncing}
          />
        </Col> */}
        <Col md={3} className="mb-4">
          <ActionCard
            title="Clear Cache"
            description="Clear synchronization cache"
            icon={Cached}
            color="secondary"
            onClick={toggleClearCacheModal}
            disabled={isSyncing}
          />
        </Col>
      </Row>

      {/* Status Information */}
      <Row>
        <Col>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <CardTitle tag="h5" className="mb-0">
                  <BarChart className="mr-2" /> System Status
                </CardTitle>
                <Button
                  color="link"
                  size="sm"
                  onClick={() => setCollapseStats(!collapseStats)}
                >
                  {collapseStats ? 'Hide Details' : 'Show Details'}
                </Button>
              </div>

              <Collapse isOpen={collapseStats}>
                {syncStatus ? (
                  <div className="row">
                    <div className="col-md-6">
                      <h6 className="mb-3 text-primary">
                        <DataUsage className="mr-2" /> Sync Status
                      </h6>
                      <Table size="sm" borderless>
                        <tbody>
                          <tr>
                            <td><strong>EMIS API:</strong></td>
                            <td className="text-right">
                              {getStatusBadge(syncStatus.emis_api?.api_status)}
                            </td>
                          </tr>
                          <tr>
                            <td><strong>Sync Status:</strong></td>
                            <td className="text-right">
                              {getStatusBadge(syncStatus.sync_status)}
                            </td>
                          </tr>
                          <tr>
                            <td><strong>Last Sync:</strong></td>
                            <td className="text-right">
                              {formatDate(syncStatus.last_sync)}
                            </td>
                          </tr>
                          <tr>
                            <td><strong>Is Running:</strong></td>
                            <td className="text-right">
                              {isSyncing ? (
                                <Badge color="warning" pill>
                                  <Sync className="mr-1 spin" /> Yes
                                </Badge>
                              ) : (
                                <Badge color="secondary" pill>No</Badge>
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>

                    <div className="col-md-6">
                      <h6 className="mb-3 text-primary">
                        <Storage className="mr-2" /> Zone Status
                      </h6>
                      <Table size="sm" borderless>
                        <tbody>
                          <tr>
                            <td><strong>Total Zones:</strong></td>
                            <td className="text-right">
                              {syncStatus.local_database?.zones_count || 0}
                            </td>
                          </tr>
                          <tr>
                            <td><strong>With Sector ID:</strong></td>
                            <td className="text-right">
                              <Badge color={syncStatus.local_database?.zones_with_sector ? 'success' : 'warning'}>
                                {syncStatus.local_database?.zones_with_sector || 0}
                              </Badge>
                            </td>
                          </tr>
                          <tr>
                            <td><strong>Without Sector ID:</strong></td>
                            <td className="text-right">
                              <Badge color={syncStatus.local_database?.zones_without_sector ? 'danger' : 'success'}>
                                {syncStatus.local_database?.zones_without_sector || 0}
                              </Badge>
                            </td>
                          </tr>
                        </tbody>
                      </Table>

                      {syncStatus.local_database?.zones_without_sector > 0 && (
                        <Alert color="warning" className="mt-3">
                          <Warning className="mr-2" />
                          <strong>Action Required:</strong> {syncStatus.local_database?.zones_without_sector} zones need sector IDs.
                          Use the "Fix Zones" button above to resolve.
                        </Alert>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Spinner size="sm" color="primary" /> Loading status...
                  </div>
                )}
              </Collapse>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Sync Modal */}
      <Modal isOpen={syncModal} toggle={toggleSyncModal} size="lg">
        <ModalHeader toggle={toggleSyncModal}>
          <Sync className="mr-2" /> Start Synchronization
        </ModalHeader>
        <ModalBody>
          <Alert color="info">
            <Info className="mr-2" />
            This will synchronize data from EMIS API to your local database.
          </Alert>

          <Form>
            <FormGroup>
              <Label for="syncMode">Sync Mode</Label>
              <div>
                <FormGroup check inline>
                  <Input
                    type="radio"
                    name="force"
                    id="normalSync"
                    checked={!syncOptions.force}
                    onChange={() => setSyncOptions({ ...syncOptions, force: false })}
                  />
                  <Label check for="normalSync" className="ml-2">
                    <strong>Normal Sync</strong>
                  </Label>
                  <FormText color="muted" className="d-block">
                    Update only new or changed records (Recommended)
                  </FormText>
                </FormGroup>
                <FormGroup check inline className="mt-2">
                  <Input
                    type="radio"
                    name="force"
                    id="forceSync"
                    checked={syncOptions.force}
                    onChange={() => setSyncOptions({ ...syncOptions, force: true })}
                  />
                  <Label check for="forceSync" className="ml-2">
                    <strong>Force Sync</strong>
                  </Label>
                  <FormText color="muted" className="d-block">
                    Re-sync all data (Use when data is corrupted)
                  </FormText>
                </FormGroup>
              </div>
            </FormGroup>

            <FormGroup>
              <FormGroup check>
                <Input
                  type="checkbox"
                  id="dryRun"
                  checked={syncOptions.dry_run}
                  onChange={(e) => setSyncOptions({ ...syncOptions, dry_run: e.target.checked })}
                />
                <Label check for="dryRun" className="ml-2">
                  <strong>Dry Run Mode</strong>
                </Label>
                <FormText color="muted">
                  Test the sync without making any changes to the database
                </FormText>
              </FormGroup>
            </FormGroup>

            <Alert color="light" className="mt-3">
              <div className="d-flex">
                <Info className="mr-2 text-primary" />
                <div>
                  <strong>Note:</strong> The sync process may take several minutes depending on
                  the amount of data. Do not close this page during synchronization.
                </div>
              </div>
            </Alert>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleSyncModal}>
            Cancel
          </Button>
          <Button
            color="success"
            onClick={startSync}
            disabled={loading || isSyncing}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="mr-1" /> Starting...
              </>
            ) : (
              <>
                {syncOptions.dry_run ? (
                  <>
                    <GetApp className="mr-1" /> Start Dry Run
                  </>
                ) : (
                  <>
                    <Sync className="mr-1" /> Start Sync
                  </>
                )}
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Fix Zones Modal */}
      <Modal isOpen={fixZonesModal} toggle={toggleFixZonesModal}>
        <ModalHeader toggle={toggleFixZonesModal}>
          <Build className="mr-2" /> Fix Zones with Missing Sector IDs
        </ModalHeader>
        <ModalBody>
          <Alert color="warning">
            <Warning className="mr-2" />
            This will update zones that don't have a sector_id by looking at their schools.
          </Alert>

          {syncStatus?.local_database?.zones_without_sector > 0 ? (
            <div className="text-center">
              <div className="display-4 text-danger mb-3">
                {syncStatus.local_database.zones_without_sector}
              </div>
              <h5 className="mb-3">zones need sector IDs</h5>
              <p className="text-muted">
                These zones will be updated based on the schools they contain.
              </p>
            </div>
          ) : (
            <Alert color="success" className="text-center">
              <CheckCircle className="mr-2" />
              All zones already have sector IDs! No action needed.
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleFixZonesModal}>
            Cancel
          </Button>
          {syncStatus?.local_database?.zones_without_sector > 0 && (
            <Button
              color="warning"
              onClick={fixZones}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="mr-1" /> Fixing...
                </>
              ) : (
                <>
                  <Build className="mr-1" /> Fix Zones
                </>
              )}
            </Button>
          )}
        </ModalFooter>
      </Modal>

      {/* Clear Cache Modal */}
      <Modal isOpen={clearCacheModal} toggle={toggleClearCacheModal}>
        <ModalHeader toggle={toggleClearCacheModal}>
          <Cached className="mr-2" /> Clear Sync Cache
        </ModalHeader>
        <ModalBody>
          <Alert color="secondary">
            <Info className="mr-2" />
            This will clear all synchronization cache data including:
          </Alert>

          <ul className="mb-0">
            <li>Sync status and progress</li>
            <li>Last sync timestamp</li>
            <li>Sync statistics</li>
            <li>Any cached API responses</li>
          </ul>

          <Alert color="warning" className="mt-3">
            <Warning className="mr-2" />
            <strong>Note:</strong> This will not delete any actual data from the database, only cache.
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleClearCacheModal}>
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={clearCache}
          >
            <Cached className="mr-1" /> Clear Cache
          </Button>
        </ModalFooter>
      </Modal>

      {/* Schedule Modal */}
      <Modal isOpen={scheduleModal} toggle={() => setScheduleModal(!scheduleModal)} size="lg">
        <ModalHeader toggle={() => setScheduleModal(false)}>
          <Timeline className="mr-2" /> Schedule EMIS Sync
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="schedule_type">Schedule Type</Label>
              <Input
                type="select"
                id="schedule_type"
                value={scheduleForm.schedule_type}
                onChange={(e) => setScheduleForm({ ...scheduleForm, schedule_type: e.target.value })}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="hourly">Every Hour</option>
              </Input>
            </FormGroup>

            {scheduleForm.schedule_type !== 'hourly' && (
              <FormGroup>
                <Label for="time">Time (24-hour format)</Label>
                <Input
                  type="time"
                  id="time"
                  value={scheduleForm.time}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, time: e.target.value })}
                />
              </FormGroup>
            )}

            {scheduleForm.schedule_type === 'weekly' && (
              <FormGroup>
                <Label for="day_of_week">Day of Week</Label>
                <Input
                  type="select"
                  id="day_of_week"
                  value={scheduleForm.day_of_week}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, day_of_week: parseInt(e.target.value) })}
                >
                  <option value="0">Sunday</option>
                  <option value="1">Monday</option>
                  <option value="2">Tuesday</option>
                  <option value="3">Wednesday</option>
                  <option value="4">Thursday</option>
                  <option value="5">Friday</option>
                  <option value="6">Saturday</option>
                </Input>
              </FormGroup>
            )}

            {scheduleForm.schedule_type === 'monthly' && (
              <FormGroup>
                <Label for="day_of_month">Day of Month</Label>
                <Input
                  type="select"
                  id="day_of_month"
                  value={scheduleForm.day_of_month}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, day_of_month: parseInt(e.target.value) })}
                >
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </Input>
              </FormGroup>
            )}

            <FormGroup check>
              <Input
                type="checkbox"
                id="enabled"
                checked={scheduleForm.enabled}
                onChange={(e) => setScheduleForm({ ...scheduleForm, enabled: e.target.checked })}
              />
              <Label check for="enabled" className="ml-2">Enable Schedule</Label>
            </FormGroup>

            <FormGroup check>
              <Input
                type="checkbox"
                id="force_sync"
                checked={scheduleForm.force_sync}
                onChange={(e) => setScheduleForm({ ...scheduleForm, force_sync: e.target.checked })}
              />
              <Label check for="force_sync" className="ml-2">Force Sync (re-sync all data)</Label>
            </FormGroup>

            <Alert color="info" className="mt-3">
              <Info className="mr-2" />
              <strong>Note:</strong> The schedule will take effect after the next cron job execution.
              Make sure your server cron is configured to run Laravel scheduler.
            </Alert>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setScheduleModal(false)}>
            Cancel
          </Button>
          <Button color="info" onClick={testSchedule}>
            Test Schedule
          </Button>
          <Button color="success" onClick={saveSchedule} disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="mr-1" /> Saving...
              </>
            ) : (
              <>
                <CheckCircle className="mr-1" /> Save Schedule
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>


    
    </Container>
  );
}

export default EmisSyncManagement;